import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import {
  getTrackingCookie,
  setTrackingCookies,
} from 'helpers/analytics/trackingCookies';
import { PWA_PARTNER_ID } from 'helpers/pwa/constants';
import {
  getIsPWAPromotionDisabled,
  getPWATrackingCookies,
  isPWAMode,
  registerPWAServiceWorker,
  removeIsPWAInstalled,
  setIsPWAInstalled,
  setIsPWAPromotionDisabled,
  setPWATrackingCookies,
} from 'helpers/pwa/utils';
import { assertNotNullObject } from 'helpers/utils/checkType';
import { usePWAAnalytics } from 'hooks/analytics/usePWAAnalytics';
import { useSystemSearchQueries } from 'hooks/useSystemSearchQueries';
import { getAuthenticated } from 'store/auth/selectors';

export interface ShowNativePromtDialogPayload {
  onShow?: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onError?: (error: any) => void;
}

interface PWAContextValue {
  isCanPromotePWA: boolean;
  isPromotionPWAShowed: boolean;
  handleDisablePromotionPWA: () => void;
  handlePWAInstalled: () => void;
  handleShowNativePromtDialogPWA: (
    payload: ShowNativePromtDialogPayload
  ) => void;
}

export const PWAContext = createContext({} as PWAContextValue);

interface DeferredPwaEvent {
  prompt: () => any;
  userChoice: Promise<{ outcome: 'accepted' | 'dissmised' }>;
}

export const PWAContextProvider: React.FC = ({ children }) => {
  const isAuthenticated = useSelector(getAuthenticated);

  const { getPartnerLoginIdQuery } = useSystemSearchQueries();

  const { trackPWAInstall } = usePWAAnalytics();

  const [isCanPromotePWA, setIsCanPromotePWA] = useState(false);
  const [isPromotionPWAShowed, setIsPromotionPWAShowed] = useState(true);
  const [isFromSystem, setIsFromSystem] = useState(true);

  const deferredEvent = useRef<DeferredPwaEvent | null>(null);

  const handleDisablePromotionPWA = () => {
    setIsPromotionPWAShowed(true);
    setIsPWAPromotionDisabled(true);
  };

  const handlePWAInstalled = useCallback(() => {
    handleDisablePromotionPWA();
    setIsCanPromotePWA(false);
  }, []);

  const handleShowNativePromtDialogPWA = useCallback(
    async (payload: ShowNativePromtDialogPayload) => {
      if (!assertNotNullObject(deferredEvent.current)) {
        throw new Error('deferredPwaInstall is not object');
      }

      if (
        !deferredEvent.current.prompt ||
        typeof deferredEvent.current.prompt !== 'function'
      ) {
        throw new Error('deferredPwaInstall have to have propmt property');
      }

      if (!deferredEvent.current.userChoice) {
        throw new Error('deferredPwaInstall have to have userChoice property');
      }

      setIsFromSystem(false);
      deferredEvent.current.prompt();
      payload.onShow && payload.onShow();

      try {
        const result = await deferredEvent.current.userChoice;

        if (result?.outcome === 'accepted') {
          const trackingCookies = getTrackingCookie();

          if (trackingCookies) {
            setPWATrackingCookies(trackingCookies);
          }
          setIsPWAInstalled(true);
          payload.onSubmit && payload.onSubmit();
        } else {
          payload.onCancel && payload.onCancel();
        }
      } catch (error: any) {
        payload.onError && payload.onError(error);
      }
      setIsFromSystem(true);
    },
    []
  );

  const handleBeforeinstallPrompt = (e: Event) => {
    removeIsPWAInstalled();

    // @ts-ignore
    deferredEvent.current = e;
    setIsCanPromotePWA(true);
    if (!getIsPWAPromotionDisabled()) {
      setIsPromotionPWAShowed(false);
    }
  };

  const handleAppInstall = useCallback(() => {
    trackPWAInstall(isFromSystem);
  }, [isFromSystem, trackPWAInstall]);

  useEffect(() => {
    /**
     * We save tracking cookies at the moment user install pwa
     * Then on click on pwa icon we add to url query parameter
     * partner_login_id=PWA_PARTNER_ID.
     * Then replace trackingCookies with saved trackingCookies in
     * LocalStorage.
     */
    const { value: partnerLoginId } = getPartnerLoginIdQuery();
    const pwaTrackingCookies = getPWATrackingCookies();

    if (Number(partnerLoginId) === PWA_PARTNER_ID && pwaTrackingCookies) {
      setTrackingCookies(pwaTrackingCookies);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isPWAMode()) {
      registerPWAServiceWorker();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    window.addEventListener('beforeinstallprompt', handleBeforeinstallPrompt);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeinstallPrompt
      );
    };
  }, []);

  useEffect(() => {
    window.addEventListener('appinstalled', handleAppInstall);

    return () => {
      window.removeEventListener('appinstalled', handleAppInstall);
    };
  }, [handleAppInstall]);

  const value = useMemo(
    () => ({
      isCanPromotePWA,
      isPromotionPWAShowed,
      handleDisablePromotionPWA,
      handlePWAInstalled,
      handleShowNativePromtDialogPWA,
    }),
    [
      handlePWAInstalled,
      handleShowNativePromtDialogPWA,
      isCanPromotePWA,
      isPromotionPWAShowed,
    ]
  );

  return <PWAContext.Provider value={value}>{children}</PWAContext.Provider>;
};
